import { Admin, Resource, Layout, type LayoutProps } from 'react-admin';
import { dataProvider } from './providers/dataProvider';
import { authProvider } from './providers/authProvider';
import { Dashboard } from './pages/Dashboard';
import { LoginPage } from './pages/LoginPage';
import { VerificationQueue } from './pages/VerificationQueue';
import { IntersectionTester } from './pages/IntersectionTester';
import { Grid3DConfigCreate } from './pages/Grid3DConfigCreate';
import { Grid2DConfigCreate } from './pages/Grid2DConfigCreate';
import { AuditLogViewer } from './pages/AuditLogViewer';
import { BulkOperations } from './pages/BulkOperations';
import { ScraperJobs } from './pages/ScraperJobs';
import { Duplicates } from './pages/Duplicates';
import { ConnectionsCreate } from './pages/ConnectionsCreate';
import { TimelineCreate } from './pages/TimelineCreate';
import { TriviaUpload } from './pages/TriviaUpload';
import { TriviaGameCreate } from './pages/TriviaGameCreate';
import { CustomMenu } from './components/CustomMenu';
import { theme } from './theme';

// Entity resources
import { EntityList } from './resources/entities/EntityList';
import { EntityEdit } from './resources/entities/EntityEdit';
import { EntityCreate } from './resources/entities/EntityCreate';
import { EntityShow } from './resources/entities/EntityShow';

// Game config resources
import { GameConfigList } from './resources/gameConfigs/GameConfigList';
import { GameConfigEdit } from './resources/gameConfigs/GameConfigEdit';
import { GameConfigCreate } from './resources/gameConfigs/GameConfigCreate';

// Admin user resources
import { AdminUserList } from './resources/adminUsers/AdminUserList';
import { AdminUserEdit } from './resources/adminUsers/AdminUserEdit';

// Icons
import ArticleIcon from '@mui/icons-material/Article';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import JoinInnerIcon from '@mui/icons-material/JoinInner';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import GridOnIcon from '@mui/icons-material/GridOn';
import HistoryIcon from '@mui/icons-material/History';
import StorageIcon from '@mui/icons-material/Storage';
import BuildIcon from '@mui/icons-material/Build';
import ExtensionIcon from '@mui/icons-material/Extension';
import TimelineIcon from '@mui/icons-material/Timeline';
import QuizIcon from '@mui/icons-material/Quiz';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const CustomLayout = (props: LayoutProps) => <Layout {...props} menu={CustomMenu} />;

function App() {
  return (
    <Admin
      dataProvider={dataProvider}
      authProvider={authProvider}
      dashboard={Dashboard}
      loginPage={LoginPage}
      theme={theme}
      layout={CustomLayout}
      requireAuth
    >
      <Resource
        name="entities"
        list={EntityList}
        edit={EntityEdit}
        create={EntityCreate}
        show={EntityShow}
        icon={ArticleIcon}
        options={{ label: 'Entities' }}
      />

      {/* Game Configs group */}
      <Resource
        name="game-configs"
        list={GameConfigList}
        edit={GameConfigEdit}
        create={GameConfigCreate}
        icon={ListAltIcon}
        options={{ label: 'All Configs' }}
      />
      <Resource
        name="connections-creator"
        list={ConnectionsCreate}
        icon={ExtensionIcon}
        options={{ label: 'Connections Creator' }}
      />
      <Resource
        name="timeline-creator"
        list={TimelineCreate}
        icon={TimelineIcon}
        options={{ label: 'Timeline Creator' }}
      />
      <Resource
        name="grid-2d-config"
        list={Grid2DConfigCreate}
        icon={GridOnIcon}
        options={{ label: '2D Grid Creator' }}
      />
      <Resource
        name="grid-3d-config"
        list={Grid3DConfigCreate}
        icon={ViewInArIcon}
        options={{ label: '3D Grid Creator' }}
      />
      <Resource
        name="intersection-tester"
        list={IntersectionTester}
        icon={JoinInnerIcon}
        options={{ label: 'Intersection Tester' }}
      />

      {/* Other resources */}
      <Resource
        name="admin-users"
        list={AdminUserList}
        edit={AdminUserEdit}
        icon={PeopleIcon}
        options={{ label: 'Admin Users' }}
      />
      <Resource
        name="verification"
        list={VerificationQueue}
        icon={VerifiedUserIcon}
        options={{ label: 'Verification Queue' }}
      />
      <Resource
        name="audit-logs"
        list={AuditLogViewer}
        icon={HistoryIcon}
        options={{ label: 'Audit Logs' }}
      />
      <Resource
        name="bulk-operations"
        list={BulkOperations}
        icon={StorageIcon}
        options={{ label: 'Bulk Operations' }}
      />
      <Resource
        name="scraper-jobs"
        list={ScraperJobs}
        icon={BuildIcon}
        options={{ label: 'Scraper Jobs' }}
      />
      <Resource
        name="trivia-game-creator"
        list={TriviaGameCreate}
        icon={EmojiObjectsIcon}
        options={{ label: 'Trivia Game Creator' }}
      />
      <Resource
        name="trivia-upload"
        list={TriviaUpload}
        icon={QuizIcon}
        options={{ label: 'Trivia Upload' }}
      />
      <Resource
        name="duplicates"
        list={Duplicates}
        icon={ContentCopyIcon}
        options={{ label: 'Duplicates' }}
      />
    </Admin>
  );
}

export default App;
